import { type FirebaseApp, initializeApp } from "firebase/app";
import {
  type Auth,
  GoogleAuthProvider,
  type User,
  signOut as firebaseSignOut,
  getAuth,
  signInWithPopup,
} from "firebase/auth";

/**
 * Firebase client configuration is served by the arena backend (or injected
 * via Vite at build time). For v1 we read from `import.meta.env` — the
 * operator populates `.env.local` with the six standard firebase web-SDK
 * values after running Unit 2's Terraform.
 */
interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId: string;
}

function readRequired(key: string): string {
  const value = (import.meta.env as Record<string, string | undefined>)[key];
  if (!value)
    throw new Error(`Firebase config missing ${key} (populate know_arena/frontend/.env.local)`);
  return value;
}

function readConfig(): FirebaseConfig {
  const env = import.meta.env as Record<string, string | undefined>;
  return {
    apiKey: readRequired("VITE_FIREBASE_API_KEY"),
    authDomain: readRequired("VITE_FIREBASE_AUTH_DOMAIN"),
    projectId: readRequired("VITE_FIREBASE_PROJECT_ID"),
    appId: readRequired("VITE_FIREBASE_APP_ID"),
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  };
}

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    _app = initializeApp(readConfig());
    _auth = getAuth(_app);
  }
  return _auth;
}

export async function signInWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  // `hd` (hosted domain) constrains the popup to accounts in the given
  // Google Workspace — Google filters out personal Gmail before the OAuth
  // round-trip completes. This is a UX hint; the backend still enforces
  // `hd === allowedDomain` on the ID token, so a misconfigured frontend
  // can't bypass the gate.
  provider.setCustomParameters({ hd: readRequired("VITE_ALLOWED_DOMAIN") });
  const result = await signInWithPopup(getFirebaseAuth(), provider);
  return result.user;
}

export async function signOut(): Promise<void> {
  await firebaseSignOut(getFirebaseAuth());
}
